import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
} from "firebase/firestore";

const HomeOffers = () => {
    const [offers, setOffers] = useState([]);
    const [categories, setCategories] = useState([]);

    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [offer, setOffer] = useState({
        title: "",
        subtitle: "",
        type: "instantDiscount",
        discountValue: "",
        isPercentage: false,
        requiredCategoryId: "",
        applicableCategoryIds: [],
        showOnHome: true,
        isActive: true,
        priority: 0,
    });

    const isServiceClubbing = offer.type === "serviceClubbing";

    /* ---------------- FETCH DATA ---------------- */

    useEffect(() => {
        fetchCategories();
        fetchOffers();
    }, []);

    const fetchCategories = async () => {
        const snapshot = await getDocs(collection(db, "Categories"));
        const data = snapshot.docs.map((doc) => ({
            id: doc.data().id ?? doc.id,
            name: doc.data().name,
        }));
        setCategories(data);
    };

    const fetchOffers = async () => {
        const snapshot = await getDocs(collection(db, "HomeOffers"));
        setOffers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };

    /* ---------------- HELPERS ---------------- */

    const handleChange = (key, value) => {
        setOffer((prev) => ({ ...prev, [key]: value }));
    };

    const toggleCategory = (categoryId) => {
        if (!categoryId) return;

        setOffer((prev) => ({
            ...prev,
            applicableCategoryIds: prev.applicableCategoryIds.includes(categoryId)
                ? prev.applicableCategoryIds.filter((id) => id !== categoryId)
                : [...prev.applicableCategoryIds, categoryId],
        }));
    };

    const resetForm = () => {
        setOffer({
            title: "",
            subtitle: "",
            type: "instantDiscount",
            discountValue: "",
            isPercentage: false,
            requiredCategoryId: "",
            applicableCategoryIds: [],
            showOnHome: true,
            isActive: true,
            priority: 0,
        });
        setEditingId(null);
        setError("");
    };

    /* ---------------- SAVE ---------------- */

    const handleSave = async (e) => {
        e.preventDefault();

        if (!offer.title || !offer.subtitle) {
            setError("Title and subtitle are required");
            return;
        }

        // if (offer.applicableCategoryIds.length === 0) {
        //     setError("Select at least one applicable category");
        //     return;
        // }

        if (isServiceClubbing && !offer.requiredCategoryId) {
            setError("Required category is mandatory for service clubbing");
            return;
        }

        setLoading(true);
        setError("");

        const payload = {
            ...offer,
            discountValue: Number(offer.discountValue),
            priority: Number(offer.priority),
        };

        try {
            if (editingId) {
                await updateDoc(doc(db, "HomeOffers", editingId), payload);
            } else {
                await addDoc(collection(db, "HomeOffers"), payload);
            }

            resetForm();
            fetchOffers();
        } catch (err) {
            console.error(err);
            setError("Failed to save offer");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- EDIT / DELETE ---------------- */

    const handleEdit = (data) => {
        setOffer({
            title: data.title ?? "",
            subtitle: data.subtitle ?? "",
            type: data.type ?? "instantDiscount",
            discountValue: data.discountValue ?? "",
            isPercentage: data.isPercentage ?? false,
            requiredCategoryId: data.requiredCategoryId ?? "",
            applicableCategoryIds: data.applicableCategoryIds ?? [],
            showOnHome: data.showOnHome ?? true,
            isActive: data.isActive ?? true,
            priority: data.priority ?? 0,
        });
        setEditingId(data.id);
    };

    const handleDelete = async (id) => {
        const confirm = window.confirm("Delete this offer?");
        if (!confirm) return;

        await deleteDoc(doc(db, "HomeOffers", id));
        fetchOffers();
    };

    /* ---------------- UI ---------------- */

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-bold mb-6">Home Offers</h1>

                {error && <p className="text-red-600 mb-4">{error}</p>}

                {/* FORM */}
                <form
                    onSubmit={handleSave}
                    className="bg-white p-6 rounded shadow mb-8 space-y-5"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                            className="p-2 border rounded"
                            placeholder="Offer Title"
                            value={offer.title}
                            onChange={(e) => handleChange("title", e.target.value)}
                        />

                        <input
                            className="p-2 border rounded"
                            placeholder="Offer Subtitle"
                            value={offer.subtitle}
                            onChange={(e) => handleChange("subtitle", e.target.value)}
                        />
                    </div>

                    <select
                        className="w-full p-2 border rounded"
                        value={offer.type}
                        onChange={(e) => handleChange("type", e.target.value)}
                    >
                        <option value="instantDiscount">Instant Discount</option>
                        <option value="serviceClubbing">Service Clubbing</option>
                    </select>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                            type="number"
                            className="p-2 border rounded"
                            placeholder="Discount Value"
                            value={offer.discountValue}
                            onChange={(e) =>
                                handleChange("discountValue", e.target.value)
                            }
                        />

                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={offer.isPercentage}
                                onChange={(e) =>
                                    handleChange("isPercentage", e.target.checked)
                                }
                            />
                            <span>Percentage Discount</span>
                        </label>
                    </div>

                    <input
                        type="number"
                        className="p-2 border rounded w-full"
                        placeholder="Priority (lower = higher priority)"
                        value={offer.priority}
                        onChange={(e) =>
                            handleChange("priority", e.target.value)
                        }
                    />

                    <hr />

                    {isServiceClubbing && (
                        <select
                            className="w-full p-2 border rounded"
                            value={offer.requiredCategoryId}
                            onChange={(e) =>
                                handleChange("requiredCategoryId", e.target.value)
                            }
                        >
                            <option value="">Select Required Category</option>
                            {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    )}

                    <div>
                        <p className="font-medium mb-2">Applicable Categories</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {categories.map((cat) => (
                                <label key={cat.id} className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={offer.applicableCategoryIds.includes(cat.id)}
                                        onChange={() => toggleCategory(cat.id)}
                                    />
                                    <span>{cat.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={offer.showOnHome}
                                onChange={(e) =>
                                    handleChange("showOnHome", e.target.checked)
                                }
                            />
                            <span>Show on Home</span>
                        </label>

                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={offer.isActive}
                                onChange={(e) =>
                                    handleChange("isActive", e.target.checked)
                                }
                            />
                            <span>Active</span>
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                        {loading
                            ? "Saving..."
                            : editingId
                                ? "Update Offer"
                                : "Create Offer"}
                    </button>
                </form>

                {/* TABLE */}
                {/* TABLE (Scrollable) */}
                <div className="flex-1 overflow-y-auto bg-white rounded shadow">
                    <table className="w-full border">
                        <thead className="bg-gray-100 sticky top-0 z-10">
                            <tr>
                                <th className="border p-2">Title</th>
                                <th className="border p-2">Type</th>
                                <th className="border p-2">Priority</th>
                                <th className="border p-2">Active</th>
                                <th className="border p-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {offers.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="p-4 text-center">
                                        No offers found
                                    </td>
                                </tr>
                            ) : (
                                offers.map((o) => (
                                    <tr key={o.id} className="border-b hover:bg-gray-50">
                                        <td className="p-2">{o.title}</td>
                                        <td className="p-2">{o.type}</td>
                                        <td className="p-2">{o.priority}</td>
                                        <td className="p-2">{o.isActive ? "Yes" : "No"}</td>
                                        <td className="p-2 space-x-3">
                                            <button
                                                onClick={() => handleEdit(o)}
                                                className="text-blue-600 hover:underline"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(o.id)}
                                                className="text-red-600 hover:underline"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    );
};

export default HomeOffers;
